import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import InputIcon from '@mui/icons-material/OpenInBrowser';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Link } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import SearchActions from '../flux/actions/SearchActions';
import SearchIcon from '@mui/icons-material/Search';
import SearchStore from '../flux/stores/SearchStore';
import classNames from 'classnames';
import moment from 'moment';

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

const Search = ({ open, handleSearch }) => {
  const [activeItem, setActiveItem] = useState(null);
  const [results, setResults] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const modalRef = useRef(null);
  const searchInputRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const debouncedSearchRef = useRef(
    debounce((term) => {
      if (term) {
        SearchActions.searchUpcoming(term);
      }
    }, 450)
  );

  // Subscribe to SearchStore
  useEffect(() => {
    function onSearchChange() {
      setResults(SearchStore.fetchSearch());
    }
    const token = SearchStore.addListener(onSearchChange);
    onSearchChange();
    return () => token.remove();
  }, []);

  // Focus management, inert background, body scroll lock, keydown
  useEffect(() => {
    if (!open) return;

    previouslyFocusedRef.current = document.activeElement;
    document.body.style.overflow = 'hidden';

    // Set siblings inert
    const modal = modalRef.current;
    if (modal) {
      const parent = modal.parentElement;
      if (parent) {
        Array.from(parent.children).forEach(child => {
          if (child !== modal) {
            child.setAttribute('inert', '');
            child.setAttribute('aria-hidden', 'true');
          }
        });
      }
    }

    requestAnimationFrame(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
        return;
      }

      if (e.key === 'Tab' && modal) {
        const focusable = modal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';

      // Remove inert from siblings
      if (modal) {
        const parent = modal.parentElement;
        if (parent) {
          Array.from(parent.children).forEach(child => {
            if (child !== modal) {
              child.removeAttribute('inert');
              child.removeAttribute('aria-hidden');
            }
          });
        }
      }

      if (previouslyFocusedRef.current && previouslyFocusedRef.current.focus) {
        previouslyFocusedRef.current.focus();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleClose() {
    setSearchTerm("");
    setResults(null);
    setActiveItem(null);
    handleSearch();
  }

  function handleChange(e) {
    const value = e.target.value;
    setSearchTerm(value);
    if (value === "") {
      setResults(null);
    } else {
      debouncedSearchRef.current(value);
    }
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }

  function handleRedirect() {
    setResults([]);
    setSearchTerm("");
    handleSearch();
  }

  function formatLabel(label, value) {
    if (!value) return label;
    return (
      <span>
        {label.split(value).reduce((prev, current, i) => {
          if (!i) return [current];
          return prev.concat(
            <strong key={value + current + i}>{value}</strong>,
            current
          );
        }, [])}
      </span>
    );
  }

  function renderResultItemSchedule(appears, appearIndex) {
    const location = appears.Location;
    const date = moment(appears.Date, "YYYY-MM-DD");
    const station = appears.Station;
    const meal = appears.Meal;
    const key = [date, appearIndex, meal].join("_");
    const linkURL = "/" + location + date.format("/YYYY/M/D/") + appears.Meal;

    return (
      <Link to={linkURL} onClick={handleRedirect} key={key} className="search-modal__appearance">
        <span className="search-modal__appearance-date">{date.format("MMM. D")}</span>
        <div className="search-modal__appearance-details">
          <span className="search-modal__appearance-name">{location} {meal}</span>
          <span className="search-modal__appearance-station">{station}</span>
        </div>
      </Link>
    );
  }

  function getItemNavMenu(id) {
    const itemURL = "/item/" + id;
    return (
      <IconButton component={Link} to={itemURL} onClick={handleRedirect} aria-label="View item details" className="search-modal__item-link">
        <InputIcon />
      </IconButton>
    );
  }

  function renderSearchResultItem(item, index) {
    const isActiveItem = item.ID === activeItem || (index === 0 && activeItem === null);
    const className = classNames("search-modal__result-item", { "search-modal__result-item--active": isActiveItem });
    const indicator = isActiveItem ? <KeyboardArrowDownIcon aria-hidden="true" /> : <KeyboardArrowRightIcon aria-hidden="true" />;
    const key = "search-result-item_" + index;

    let appearances = null;
    if (isActiveItem) {
      appearances = (
        <div className="search-modal__appearances animated fadeIn">
          {item.ItemSchedule.ItemAppearance.map(renderResultItemSchedule)}
        </div>
      );
    }

    return (
      <div key={key + item.ID} className={className} role="listitem">
        <div className="search-modal__result-row">
          <button
            className="search-modal__result-name"
            onClick={() => setActiveItem(item.ID)}
            aria-expanded={isActiveItem}
            aria-label={`${item.Name} - ${isActiveItem ? 'collapse' : 'expand'} details`}
          >
            {indicator}
            <div>{formatLabel(item.Name.toLowerCase(), searchTerm.toLowerCase())}</div>
          </button>
          {getItemNavMenu(item.ID)}
        </div>
        {appearances}
      </div>
    );
  }

  function renderResults() {
    if (results === null) return null;
    if (results.length === 0) {
      return (
        <div className="search-modal__no-results" role="status" aria-live="polite">
          No results found
        </div>
      );
    }
    return (
      <div className="search-modal__results-list" role="list" aria-label={`${results.length} search results found`}>
        <h2 className="sr-only">Search Results</h2>
        {results.map(renderSearchResultItem)}
      </div>
    );
  }

  if (!open) return null;

  return (
    <div
      className="search-modal__overlay"
      onClick={handleBackdropClick}
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-modal-heading"
    >
      <div className="search-modal__container">
        <div className="search-modal__header">
          <h1 id="search-modal-heading" className="sr-only">Search Menu Items</h1>
          <SearchIcon className="search-modal__search-icon" aria-hidden="true" />
          <input
            ref={searchInputRef}
            id="searchTerm"
            name="searchTerm"
            type="search"
            className="search-modal__input"
            value={searchTerm}
            placeholder="Search for upcoming items..."
            onChange={handleChange}
            aria-label="Search for upcoming menu items"
            autoComplete="off"
          />
          <button
            className="search-modal__close-btn"
            onClick={handleClose}
            aria-label="Close search"
            type="button"
          >
            <CloseIcon aria-hidden="true" />
          </button>
        </div>
        <div className="search-modal__body">
          {renderResults()}
        </div>
      </div>
    </div>
  );
};

export default Search;